import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './BlogPage.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8081';
const PAGE_SIZE = 6;

// Helper functions
function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { 
    month: 'long', day: 'numeric', year: 'numeric' 
  });
}

function getExcerpt(post) {
  if (post.postExcerpt) return post.postExcerpt.replace(/<[^>]*>/g, '');
  if (post.postContent) return post.postContent.replace(/<[^>]*>/g, '').slice(0, 160);
  return '';
}

function createSlug(title) {
  if (!title) return '';
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-');
}

// Blog Card Component
function BlogCard({ post }) {
  const title = post.postTitle || post.title;
  const image = post.postImage;
  const [imgError, setImgError] = useState(false);
  const slug = createSlug(title);

  return (
    <article className="blog-card-new">
      <Link to={`/${slug}`} className="blog-card-link">
        <div className="blog-card-image">
          {image && !imgError ? (
            <img src={image} alt={title} onError={() => setImgError(true)} />
          ) : (
            <div className="blog-card-image-placeholder">📷</div>
          )}
        </div>
        <div className="blog-card-content">
          <div className="blog-card-meta">
            <span className="blog-card-date">{formatDate(post.postDate)}</span>
            <span className="blog-card-category">{post.categoryName || 'Blog'}</span>
          </div>
          <h2 className="blog-card-title">{title}</h2>
          <p className="blog-card-excerpt">{getExcerpt(post)}</p>
          <span className="blog-card-readmore">Read More →</span>
        </div>
      </Link>
    </article>
  );
}

// Pagination Component
function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(0, endPage - maxVisible + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="blog-pagination">
      <button className="pagination-btn" disabled={currentPage === 0} onClick={() => onPageChange(0)}>⏮ First</button>
      <button className="pagination-btn" disabled={currentPage === 0} onClick={() => onPageChange(currentPage - 1)}>← Prev</button>
      <div className="pagination-numbers">
        {getPageNumbers().map(pageNum => (
          <button key={pageNum} className={`pagination-number ${currentPage === pageNum ? 'active' : ''}`} onClick={() => onPageChange(pageNum)}>
            {pageNum + 1}
          </button>
        ))}
      </div>
      <button className="pagination-btn" disabled={currentPage >= totalPages - 1} onClick={() => onPageChange(currentPage + 1)}>Next →</button>
      <button className="pagination-btn" disabled={currentPage >= totalPages - 1} onClick={() => onPageChange(totalPages - 1)}>Last ⏭</button>
    </div>
  );
}

// Main Category Page Component
export default function CategoryPage() {
  const { categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    const pageParam = searchParams.get('page');
    if (pageParam) {
      setCurrentPage(parseInt(pageParam) || 0);
    }
  }, [searchParams]);

  useEffect(() => {
    if (categorySlug) {
      fetchCategoryInfo();
      fetchPostsByCategory();
    }
  }, [categorySlug, currentPage]);

  const fetchCategoryInfo = async () => {
    try {
      const res = await axios.get(`${API}/api/categories/slug/${categorySlug}`);
      setCategory(res.data);
    } catch (error) {
      console.error('Error fetching category:', error);
      setError(true);
    }
  };

  const fetchPostsByCategory = async () => {
    setLoading(true);
    setError(false);
    
    try {
      const res = await axios.get(`${API}/api/categories/${categorySlug}/posts`, {
        params: { page: currentPage, size: PAGE_SIZE }
      });
      
      const items = res.data.posts || [];
      const total = res.data.totalItems || items.length;
      const totalPagesCount = res.data.totalPages || Math.ceil(total / PAGE_SIZE);
      
      setPosts(items);
      setTotalCount(total);
      setTotalPages(totalPagesCount);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setSearchParams({ page: newPage.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="blog-page-new">
        <div className="blog-container-new">
          <main className="blog-main-new">
            <div className="blog-header-new">
              <h1>Loading...</h1>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="blog-page-new">
        <div className="blog-container-new">
          <main className="blog-main-new">
            <div className="error-state">
              <p>⚠️ Category not found or error loading posts.</p>
              <Link to="/blog" className="back-to-blog">← Back to All Posts</Link>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page-new">
      <div className="blog-container-new">
        <main className="blog-main-new">
          <div className="blog-header-new">
            <h1>
              <span className="category-icon">{category.icon || '📁'}</span>
              {category.name}
            </h1>
            <p>
              {totalCount} {totalCount === 1 ? 'post' : 'posts'} in this category
              {category.description && ` - ${category.description}`}
            </p>
            <Link to="/blog" className="back-to-blog">← View all posts</Link>
          </div>

          {posts.length === 0 ? (
            <div className="empty-state">
              <p>📁 No posts found in this category yet.</p>
            </div>
          ) : (
            <>
              {posts.map(post => (
                <BlogCard key={post.id} post={post} />
              ))}
              
              {totalPages > 1 && (
                <Pagination 
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
              
              <div className="pagination-info">
                Showing {posts.length} of {totalCount} posts in {category.name}
              </div>
            </>
          )}
        </main>

        <aside className="blog-sidebar-new">
          <div className="sidebar-widget-new">
            <h3>🔍 Search</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const input = e.target.querySelector('input');
              const searchTerm = input.value.trim();
              if (searchTerm) {
                window.location.href = `/search?q=${encodeURIComponent(searchTerm)}`;
              }
            }}>
              <input type="text" placeholder="Search articles..." className="sidebar-search-input" />
            </form>
          </div>
          
          <div className="sidebar-widget-new">
            <h3>📁 All Categories</h3>
            <div className="categories-list-simple" id="categories-list">
              {/* Categories will be loaded via JS or you can create a separate component */}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}